import type { RestProduct } from "@KassaroAB/shared/types/product";

import type {
  ProductStoreRequest,
  ProductStoreResponse,
  ProductQuery,
  ProductQueryResult,
  ProductStoreMeta,
  ProductGroupNameRow,
} from "./productStore.types";

type ProductStoreRequestPayload = ProductStoreRequest extends infer T
  ? T extends ProductStoreRequest
    ? Omit<T, "id">
    : never
  : never;

export class ProductStore {
  private worker: Worker;
  private nextId = 0;
  private pending = new Map<
    number,
    { resolve: (value: ProductStoreResponse) => void; reject: (error: Error) => void }
  >();

  constructor() {
    this.worker = new Worker(
      new URL("./productStore.worker.ts", import.meta.url),
      { type: "module" },
    );
    this.worker.onmessage = (event: MessageEvent<ProductStoreResponse>) => {
      const response = event.data;
      const handlers = this.pending.get(response.id);
      if (!handlers) {
        return;
      }
      this.pending.delete(response.id);
      if (response.type === "error") {
        handlers.reject(new Error(response.message));
        return;
      }
      handlers.resolve(response);
    };
  }

  private request(
    request: ProductStoreRequestPayload,
  ): Promise<ProductStoreResponse> {
    const id = this.nextId++;
    return new Promise<ProductStoreResponse>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker.postMessage({ ...request, id });
    });
  }

  async init(storeId: string): Promise<ProductStoreMeta> {
    const response = await this.request({ type: "init" as const, storeId });
    return response.type === "init" ? response.meta : { count: 0, maxUpdatedAt: null };
  }

  async upsertMany(products: RestProduct[]): Promise<void> {
    await this.request({ type: "upsertMany", products });
  }

  async deleteMany(ids: string[]): Promise<void> {
    await this.request({ type: "deleteMany", ids });
  }

  async query(query: ProductQuery): Promise<ProductQueryResult> {
    const response = await this.request({ type: "query", query });
    return response.type === "query" ? response.result : { rows: [], total: 0 };
  }

  async meta(): Promise<ProductStoreMeta> {
    const response = await this.request({ type: "meta" });
    return response.type === "meta" ? response.meta : { count: 0, maxUpdatedAt: null };
  }

  async clear(): Promise<void> {
    await this.request({ type: "clear" });
  }

  async setGroups(groups: ProductGroupNameRow[]): Promise<void> {
    await this.request({ type: "setGroups", groups });
  }

  terminate(): void {
    this.worker.terminate();
    this.pending.clear();
  }
}
